/**
 * Animations pédagogiques (moteur JRAF) : brouillons créés par l'IA depuis
 * une SKILLCARD (table `animations`, migration 0659 ; edge fn
 * `animation-generate`), publiés dans l'app (lot 4 : edge fn
 * `animation-publish`, versions immuables, migration 0666). Admins seulement
 * ici ; la lecture élève / visiteur passe par `published.ts`.
 *
 * La génération tourne en tâche de fond : on relit la ligne toutes les
 * 3 secondes tant qu'elle est `generating`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { autoFixJraf, validateJraf } from '@jeraisonne/animation';
import { supabase } from '@/lib/supabase';
import { publishErrorMessage } from './publish-errors';

/** `animations` (0659) n'est pas dans les types générés : client non typé, comme card_tools. */
const db = supabase as unknown as SupabaseClient;

export type AnimationGenerationStatus = 'idle' | 'generating' | 'ready' | 'failed';

export interface AnimationIssue {
  path: string;
  code: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface AnimationRow {
  id: string;
  card_id: string;
  title: string;
  status: 'draft' | 'review' | 'published' | 'archived';
  draft: unknown;
  draft_valid: boolean;
  /**
   * `quality` : défauts de mise en page, de rythme ou de figure à la fin de la
   * génération (0666) ; `voices` : après « Corriger avec l'IA », les voix
   * gardées et celles retirées (texte changé : à réenregistrer)
   */
  validation: {
    errors: AnimationIssue[];
    warnings: AnimationIssue[];
    quality?: number;
    voices?: { kept: string[]; dropped: string[] };
  } | null;
  generation_status: AnimationGenerationStatus;
  generation_step: string | null;
  generation_error: string | null;
  generated_at: string | null;
  updated_at: string;
  /** lien public `/animation/<slug>`, choisi à la 1re publication (0666) */
  slug: string | null;
  /** version servie quand `status = 'published'` */
  published_version: number | null;
  /** = updated_at juste après la publication */
  published_at: string | null;
  /** « J'ai relu » (0672) : relu ⇔ reviewed_at >= updated_at */
  reviewed_at?: string | null;
}

const COLUMNS =
  'id, card_id, title, status, draft, draft_valid, validation, generation_status, generation_step, generation_error, generated_at, updated_at, slug, published_version, published_at, reviewed_at';

/** Relu par un admin depuis la dernière modification (0672) : publiable. */
export function isReviewed(row: Pick<AnimationRow, 'reviewed_at' | 'updated_at'>): boolean {
  return (
    !!row.reviewed_at && new Date(row.reviewed_at).getTime() >= new Date(row.updated_at).getTime()
  );
}

/** « J'ai relu » : le brouillon en l'état est relu (0672). */
export function useMarkReviewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.rpc('mark_animation_reviewed', { p_animation_id: id });
      if (error) throw new AnimationFunctionError(error.message);
      return id;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['animations'] }),
  });
}

/** Une version publiée (0666), du plus récent au plus ancien. */
export interface AnimationVersionRow {
  version_no: number;
  published_at: string;
  runtime_min: number;
}

export function useAnimationVersions(animationId: string, enabled = true) {
  return useQuery({
    queryKey: ['animations', 'versions', animationId] as const,
    enabled: enabled && !!animationId,
    queryFn: async (): Promise<AnimationVersionRow[]> => {
      const { data, error } = await db
        .from('animation_versions')
        .select('version_no, published_at, runtime_min')
        .eq('animation_id', animationId)
        .order('version_no', { ascending: false });
      if (error) throw new AnimationFunctionError(error.message);
      return (data ?? []) as AnimationVersionRow[];
    },
  });
}

/**
 * « Reprendre cette version » : le document d'une version publiée redevient
 * le brouillon (revalidé) ; la version en ligne ne change pas avant de
 * republier.
 */
export function useRestoreAnimationVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; versionNo: number; toolIds?: readonly string[] }) => {
      const { data, error } = await db
        .from('animation_versions')
        .select('doc')
        .eq('animation_id', input.id)
        .eq('version_no', input.versionNo)
        .maybeSingle();
      if (error) throw new AnimationFunctionError(error.message);
      if (!data?.doc) throw new AnimationFunctionError('Version introuvable.');
      const c = checkedDraft(data.doc, input.toolIds);
      const { error: upErr } = await db
        .from('animations')
        .update({
          draft: c.draft,
          draft_valid: c.valid,
          validation: { errors: c.errors, warnings: c.warnings },
        })
        .eq('id', input.id);
      if (upErr) throw new AnimationFunctionError(upErr.message);
      return input.versionNo;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['animations'] }),
  });
}

/** Une génération sans nouvelle depuis ce délai est considérée morte (même seuil que le serveur). */
export const ANIMATION_STALE_MS = 6 * 60 * 1000;

export function isGenerationStale(
  row: Pick<AnimationRow, 'generation_status' | 'updated_at'>,
  now = Date.now(),
) {
  return (
    row.generation_status === 'generating' &&
    now - new Date(row.updated_at).getTime() >= ANIMATION_STALE_MS
  );
}

export function isGenerating(
  row: Pick<AnimationRow, 'generation_status' | 'updated_at'> | null | undefined,
): boolean {
  return !!row && row.generation_status === 'generating' && !isGenerationStale(row);
}

export const GENERATION_STEP_LABELS: Record<string, string> = {
  plan: 'Plan des scènes (méthode HORA)',
  revise: 'Correction par l’IA',
  document: 'Écriture de l’animation',
  check: 'Vérification (calculs, mise en page, rythme)',
  repair_1: 'Corrections (1/3)',
  repair_2: 'Corrections (2/3)',
  repair_3: 'Corrections (3/3)',
  done: 'Terminé',
};

export const animationKeys = {
  card: (cardId: string) => ['animations', 'card', cardId] as const,
  one: (id: string) => ['animations', 'one', id] as const,
  list: (archived: boolean) => ['animations', 'list', archived] as const,
};

const poll = (row: AnimationRow | null | undefined) => (isGenerating(row) ? 3000 : false);

/** L'animation vivante (non archivée) d'une carte, ou `null`. */
export function useCardAnimation(cardId: string, enabled = true) {
  return useQuery({
    queryKey: animationKeys.card(cardId),
    enabled,
    queryFn: async (): Promise<AnimationRow | null> => {
      const { data, error } = await db
        .from('animations')
        .select(COLUMNS)
        .eq('card_id', cardId)
        .neq('status', 'archived')
        .maybeSingle();
      if (error) throw error;
      return (data as AnimationRow | null) ?? null;
    },
    refetchInterval: (q) => poll(q.state.data),
  });
}

export function useAnimation(id: string) {
  return useQuery({
    queryKey: animationKeys.one(id),
    queryFn: async (): Promise<AnimationRow | null> => {
      const { data, error } = await db
        .from('animations')
        .select(COLUMNS)
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data as AnimationRow | null) ?? null;
    },
    refetchInterval: (q) => poll(q.state.data),
  });
}

const GENERATE_ERROR_MESSAGES: Record<string, string> = {
  budget_exceeded: 'Le budget IA du jour est atteint. Réessaie demain.',
  already_running: 'Une génération est déjà en cours pour cette animation.',
  card_not_found: 'Carte introuvable.',
  not_found: 'Animation introuvable.',
  forbidden: 'Réservé aux administrateurs.',
  invalid_input: 'Consignes trop longues (2 000 caractères au plus).',
  no_draft: 'Rien à corriger : l’animation n’a pas encore de brouillon.',
};

export class AnimationFunctionError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'AnimationFunctionError';
  }
}

export type GenerateAnimationBody =
  | {
      card_id: string;
      instructions?: string;
      model?: string;
      /** monologue ou dialogue à 2 / 3 (`@/lib/animations/voices`) */
      voices?: { mode: 'monologue' | 'dialogue'; roles: string[]; notes?: string };
    }
  | { animation_id: string }
  /**
   * « Corriger avec l'IA » : part du brouillon actuel (tes corrections, tes
   * voix) ; consigne facultative (sans : les défauts sont corrigés) ; portée :
   * toute l'animation ou une scène
   */
  | {
      animation_id: string;
      revise: { instructions?: string; scene_id?: string };
      model?: string;
    };

/** Lance (ou relance) la génération ; répond tout de suite, la suite se lit par `useCardAnimation`. */
export function useGenerateAnimation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: GenerateAnimationBody) => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        animation_id: string;
      }>('animation-generate', { body });
      if (error) {
        let code: string | undefined;
        let message = error.message;
        const ctx = (error as Error & { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const parsed = (await ctx.json()) as { error?: string; message?: string };
            code = parsed.error;
            message = parsed.message ?? parsed.error ?? message;
          } catch {
            /* message générique */
          }
        }
        throw new AnimationFunctionError((code && GENERATE_ERROR_MESSAGES[code]) || message, code);
      }
      if (!data?.animation_id) throw new AnimationFunctionError('Réponse vide du serveur.');
      return data.animation_id;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['animations'] }),
  });
}

// ── Rubrique admin « Animations SKILLCARDS » ─────────────────────────────────

/** Une animation de la liste admin, avec sa SKILLCARD. */
export interface AnimationListRow extends AnimationRow {
  card: { id: string; title: string; subject: string | null; levels: string[] } | null;
}

interface RawCard {
  id: string;
  title: string;
  subject: string | null;
  card_levels: { level: string }[] | null;
}

interface RawListRow extends AnimationRow {
  /** relation « plusieurs vers un » : un objet (un tableau selon les versions du client) */
  card: RawCard | RawCard[] | null;
}

/** Toutes les animations (les archivées sur demande), les plus récentes d'abord. */
export function useAnimationsList(includeArchived = false) {
  return useQuery({
    queryKey: animationKeys.list(includeArchived),
    queryFn: async (): Promise<AnimationListRow[]> => {
      let q = db
        .from('animations')
        .select(`${COLUMNS}, card:cards(id, title, subject, card_levels(level))`)
        .order('updated_at', { ascending: false })
        .limit(500);
      if (!includeArchived) q = q.neq('status', 'archived');
      const { data, error } = await q;
      if (error) throw error;
      return ((data ?? []) as unknown as RawListRow[]).map(({ card: raw, ...row }) => {
        const card = Array.isArray(raw) ? (raw[0] ?? null) : raw;
        return {
          ...row,
          card: card
            ? {
                id: card.id,
                title: card.title,
                subject: card.subject,
                levels: (card.card_levels ?? []).map((l) => l.level),
              }
            : null,
        };
      });
    },
    // une génération en cours dans la liste : on relit
    refetchInterval: (q) => (q.state.data?.some((r) => isGenerating(r)) ? 5000 : false),
  });
}

/** Supprime une animation (brouillon et état de génération) ; la carte, elle, reste. */
export function useDeleteAnimation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.from('animations').delete().eq('id', id);
      if (error) throw new AnimationFunctionError(error.message);
      return id;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['animations'] }),
  });
}

/** Enregistre un brouillon modifié à la main, avec le résultat de sa validation. */
export function useSaveAnimationDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      draft: unknown;
      valid: boolean;
      errors: AnimationIssue[];
      warnings: AnimationIssue[];
    }) => {
      const { error } = await db
        .from('animations')
        .update({
          draft: input.draft,
          draft_valid: input.valid,
          validation: { errors: input.errors, warnings: input.warnings },
        })
        .eq('id', input.id);
      if (error) throw new AnimationFunctionError(error.message);
      return input.id;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['animations'] }),
  });
}

/**
 * Brouillon changé par l'app (voix enregistrée, tailles réduites, JSON
 * modifié) prêt à enregistrer : erreurs mécaniques corrigées d'office
 * (`autoFixJraf`), puis revalidé (valeurs par défaut posées s'il est
 * valide, rapport à jour).
 */
export function checkedDraft(input: unknown, toolIds?: readonly string[]) {
  // erreurs mécaniques (« draw » sur un texte…) corrigées d'office, comme à la création
  const draft = autoFixJraf(input).doc;
  const r = validateJraf(draft, toolIds ? { toolIds } : {});
  return {
    draft: r.ok ? (r.doc as unknown) : draft,
    valid: r.ok,
    errors: r.ok ? [] : r.errors,
    warnings: r.warnings,
  };
}

// ── Publication dans l'app (lot 4, migration 0666) ──────────────────────────

/** En ligne, et le brouillon a changé depuis la publication. */
export function hasUnpublishedChanges(
  row: Pick<AnimationRow, 'status' | 'published_at' | 'updated_at'>,
): boolean {
  return (
    row.status === 'published' &&
    !!row.published_at &&
    new Date(row.updated_at).getTime() > new Date(row.published_at).getTime()
  );
}

export interface PublishResult {
  slug: string;
  version_no: number;
  runtime_min: number;
  locked: number;
  warnings: number;
}

/** « Publier dans l'app » : le serveur revalide, écrit une version immuable, met en ligne. */
export function usePublishAnimation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<PublishResult> => {
      const { data, error } = await supabase.functions.invoke<PublishResult & { ok: boolean }>(
        'animation-publish',
        { body: { animation_id: id } },
      );
      if (error) {
        let code: string | undefined;
        let detail: string | undefined;
        const ctx = (error as Error & { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const parsed = (await ctx.json()) as { error?: string; message?: string };
            code = parsed.error;
            detail = parsed.message;
          } catch {
            /* message générique */
          }
        }
        throw new AnimationFunctionError(publishErrorMessage(code, detail, error.message), code);
      }
      if (!data?.slug) throw new AnimationFunctionError('Réponse vide du serveur.');
      return data;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['animations'] }),
  });
}

/** « Retirer de l'app » : plus servie (la version reste ; republier la remet en ligne). */
export function useUnpublishAnimation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db
        .from('animations')
        .update({ status: 'draft' })
        .eq('id', id)
        .eq('status', 'published');
      if (error) throw new AnimationFunctionError(error.message);
      return id;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['animations'] }),
  });
}
